import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import AttachFile from '@material-ui/icons/AttachFile';
import Description from '@material-ui/icons/Description';
import MailOutline from '@material-ui/icons/MailOutline';
import Person from '@material-ui/icons/Person';
import Send from '@material-ui/icons/Send';
import validator from 'validator';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import {
  FileNode,
  ICompany,
  IUser,
  ProjectLifecycleFormType,
} from '../../api-client/autogenerated';
import { getProjectState } from '../../features/project/selectors';
import { getUserState } from '../../features/user/selectors';
import { getCompaniesState } from '../../features/companies/selector';
import { addSnackbar } from '../../features/snackbar/actions';
import {
  DocumentFileTree,
  getDocumentTreesForAssociations,
  STORYTELLING_TREE_TYPES,
} from '../../scripts/utils';
import {
  getStorytellingShareTrees,
  parseStorytellingFormId,
} from '../project-lifecycle/share-tree';
import { shareDocumentPublicLinks } from '../../models/api/documents';
import AssociateDocumentDialog from '../add-associations/AssociateDocumentDialog';
import SelectCompanyUsersDialog from '../dialogs/SelectCompanyUsersDialog';
import AddDesignFileDialog from '../dialogs/AddDesignFileDialog';
import CircularLoader from '../loader/CircularLoader';
import RemovableChip from '../custom-components/RemovableChip';
import { CancelButton } from '../custom-components/CustomButtons';
import { GreenCheck } from '../custom-components/CustomCheckboxes';

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#FFFFFF',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    height: '100%',
  },
  titleStyle: {
    background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
    borderRadius: '4px 4px 0px 0px',
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-start',
    borderBottom: '1px solid #EDECEC',
    padding: '8px 0px',
    // On a phone the label's fixed column leaves too little beside it: the button label wrapped
    // onto two lines and the email and subject fields were squeezed to a few characters. Each
    // row stacks instead, so every control gets the card's full width under its own label.
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  fieldLabel: {
    color: '#0947B9',
    fontWeight: 500,
    // Wide enough for "Add External Collaborators:", so every row's control starts on the same
    // line rather than each label sizing itself and leaving the column ragged.
    flex: '0 0 190px',
    paddingTop: 6,
    paddingRight: 8,
    [theme.breakpoints.down('xs')]: {
      flex: '0 0 auto',
      paddingTop: 0,
      paddingBottom: 4,
    },
  },
  emptyRecipients: {
    color: '#949494', // Gray 400
    fontStyle: 'italic',
    paddingTop: 6,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 4,
    flexGrow: 1,
    // Without this the widest pill sets the row's min-content width and pushes the card wider than
    // the phone rather than wrapping inside it.
    minWidth: 0,
  },
  /**
   * The recipients button and the copy-to-me checkbox: side by side while the card is wide enough,
   * and the checkbox drops underneath the button rather than squeezing it on a phone.
   */
  recipientRow: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    minWidth: 0,
  },
  /** Cancel at one end, Send at the other. */
  sendRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
  },
  /** Wraps rather than squeezing both attachment buttons onto one phone-width line. */
  attachButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  /**
   * The address field and its Add button. On a phone the button beside it left the field too
   * narrow to hold its own placeholder on one line, so the button drops underneath and the field
   * gets the card's width.
   */
  emailEntry: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  /** Fills whatever the Add button leaves, and the whole row once that button drops below it. */
  emailField: {
    flexGrow: 1,
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
}));

/** Anchor for the Field Tools card's "Send Anything" button, which scrolls the page down here. */
export const SHARE_ANYTHING_CARD_ID = 'share-anything-card';

type Attachment = {
  /**
   * Stable key for de-duplication and removal: the document id, the file's full key, or the
   * storytelling form's `<formType>:<formId>` pair.
   */
  key: string;
  label: string;
  documentId?: string;
  fileNode?: { fullKey: string; ownerId?: string };
  /** A storytelling form, which travels as a public project link rather than a document link. */
  projectLifecycleForm?: { formType: ProjectLifecycleFormType; formId?: string };
};

export default function DashboardShareDocuments() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const project = useSelector(getProjectState);
  const user = useSelector(getUserState);
  const companies = useSelector(getCompaniesState);

  const [documentTrees, setDocumentTrees] = useState<DocumentFileTree[]>([]);
  const [isLoadingTrees, setIsLoadingTrees] = useState(true);

  // Kept apart because they are added differently and mean different things: a team member is
  // someone on the project, an external collaborator is only ever an address.
  const [teamMemberIds, setTeamMemberIds] = useState<string[]>([]);
  const [externalEmails, setExternalEmails] = useState<string[]>([]);
  const [inputEmail, setInputEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [sendCopyToSelf, setSendCopyToSelf] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const [recipientDialogOpen, setRecipientDialogOpen] = useState(false);
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [fileDialogOpen, setFileDialogOpen] = useState(false);

  // Storytelling only where the subscriber has the module, which is also what decides whether the
  // dialog lists the phase at all.
  const showStorytelling = !!project?.subscriber?.canViewProjectLifecycle;

  useEffect(() => {
    if (project?.id) {
      setIsLoadingTrees(true);
      // Every phase: a share sends a public link, so a design task or an addendum is as
      // shareable as a submittal. Only this card passes the flag.
      getDocumentTreesForAssociations(project.id, undefined, undefined, true)
        .then((trees) =>
          setDocumentTrees(
            showStorytelling ? [...trees, ...getStorytellingShareTrees(project)] : trees,
          ),
        )
        .finally(() => setIsLoadingTrees(false));
    }
  }, [project?.id, showStorytelling]);

  const defaultSubject = project?.name ? `Documents shared from ${project.name}` : '';

  useEffect(() => {
    if (defaultSubject) setSubject(defaultSubject);
  }, [defaultSubject]);

  /** Back to a blank composer, subject included -- the state the card opens in. */
  const clearComposer = () => {
    setTeamMemberIds([]);
    setExternalEmails([]);
    setInputEmail('');
    setEmailError('');
    setAttachments([]);
    setSendCopyToSelf(false);
    setSubject(defaultSubject);
    setBody('');
  };

  const usersById = useMemo(() => {
    const entries = new Map<string, IUser>();
    (companies as ICompany[]).forEach((company) =>
      (company.users ?? []).forEach((user) => entries.set(user.id, user)),
    );
    return entries;
  }, [companies]);

  const teamMemberEmails = useMemo(
    () => _.compact(teamMemberIds.map((id) => usersById.get(id)?.email)),
    [teamMemberIds, usersById],
  );

  /**
   * Addresses pasted out of a mail client arrive separated by commas or semicolons, so the field
   * takes a list. Every address is reported on: silently dropping a bad one in a pasted batch is
   * how a recipient goes missing without anyone noticing.
   */
  const handleAddTypedEmails = () => {
    const candidates = inputEmail
      .split(/[,;]/)
      .map((value) => value.trim())
      .filter(Boolean);
    if (candidates.length === 0) return;

    const invalid = candidates.filter((value) => !validator.isEmail(value));
    if (invalid.length > 0) {
      return setEmailError(
        invalid.length === 1
          ? `Invalid email: ${invalid[0]}`
          : `Invalid emails: ${invalid.join(', ')}`,
      );
    }
    const known = new Set([...externalEmails, ...teamMemberEmails]);
    const fresh = candidates.filter((value) => !known.has(value));
    if (fresh.length === 0) return setEmailError('Already added');

    setExternalEmails((previous) => Array.from(new Set([...previous, ...fresh])));
    setInputEmail('');
    setEmailError('');
  };

  const addAttachments = (incoming: Attachment[]) => {
    setAttachments((previous) => {
      const known = new Set(previous.map((a) => a.key));
      return [...previous, ...incoming.filter((a) => !known.has(a.key))];
    });
  };

  // The tree dialog owns selection as `checked` flags; lift the ticked ones out as attachments and
  // clear them, so the tree is ready to pick from again and the card is the single source of truth.
  const takeCheckedDocuments = () => {
    const picked: Attachment[] = documentTrees.flatMap((tree) =>
      tree.documents
        .filter((document) => document.checked)
        .map((document) =>
          STORYTELLING_TREE_TYPES.includes(tree.type)
            ? {
                key: document.id,
                // Inside a group the tree shows only the date, the heading saying what it is;
                // an attachment has to name itself in the share email.
                label: document.userFriendlyName || document.title,
                projectLifecycleForm: parseStorytellingFormId(document.id),
              }
            : {
                key: document.id,
                label: document.userFriendlyName || document.title,
                documentId: document.id,
              },
        ),
    );
    if (picked.length > 0) addAttachments(picked);
    setDocumentTrees((previous) =>
      previous.map((tree) => ({
        ...tree,
        documents: tree.documents.map((document) => ({ ...document, checked: false })),
      })),
    );
  };

  const handleSend = async () => {
    if (!project) return;
    setIsSending(true);
    try {
      await shareDocumentPublicLinks({
        projectId: project.id,
        documentIds: attachments.filter((a) => a.documentId).map((a) => a.documentId!),
        fileNodes: attachments.filter((a) => a.fileNode).map((a) => a.fileNode!),
        projectLifecycleForms: attachments
          .filter((a) => a.projectLifecycleForm)
          .map((a) => a.projectLifecycleForm!),
        // A copy to the sender is the sender as a recipient: they are on the project, so they get
        // the notification the other team members get rather than a second kind of email.
        userIds: sendCopyToSelf ? _.uniq([...teamMemberIds, user.id]) : teamMemberIds,
        emails: externalEmails,
        subject,
        body,
      });
      dispatch(addSnackbar({ id: Date.now(), message: 'Share sent', severity: 'success' }));
      clearComposer();
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Everyone the share is addressed to, in one list.
   *
   * The two sections above are how a recipient is added; this is who was added. Keeping them apart
   * meant reading two places to answer "who is this going to", which is the question the composer
   * is actually asking. The icon is what still distinguishes them once they are side by side.
   */
  const recipients = useMemo(
    () => [
      // The sender, when they asked for a copy: they are a recipient like any other, so the list
      // that answers "who is this going to" has to say so.
      ...(sendCopyToSelf && !teamMemberIds.includes(user.id)
        ? [
            {
              key: `user:${user.id}`,
              label: `${user.name || user.email} (you)`,
              icon: <Person fontSize="small" />,
              remove: () => setSendCopyToSelf(false),
            },
          ]
        : []),
      ...teamMemberIds.map((id) => ({
        key: `user:${id}`,
        label: usersById.get(id)?.name || usersById.get(id)?.email || id,
        icon: <Person fontSize="small" />,
        remove: () => setTeamMemberIds((previous) => previous.filter((value) => value !== id)),
      })),
      ...externalEmails.map((email) => ({
        key: `email:${email}`,
        label: email,
        icon: <MailOutline fontSize="small" />,
        remove: () => setExternalEmails((previous) => previous.filter((value) => value !== email)),
      })),
    ],
    [teamMemberIds, externalEmails, usersById, sendCopyToSelf, user],
  );

  const canSend = recipients.length > 0 && attachments.length > 0 && !isSending;

  return (
    <Card className={classes.root} id={SHARE_ANYTHING_CARD_ID}>
      <CardContent className={classes.titleStyle}>
        <Grid container style={{ justifyContent: 'space-between' }}>
          <Grid item>
            <h2 className="h2" style={{ textAlign: 'left', color: '#FFFFFF', margin: 0 }}>
              Send Anything to Anyone
            </h2>
          </Grid>
        </Grid>
      </CardContent>
      <CardContent>
        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Add Project Team Members:</Typography>
          <div className={classes.recipientRow}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disableElevation
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => setRecipientDialogOpen(true)}
            >
              Add Recipients
            </Button>
            {/* Beside the button while there is room, and underneath it on a phone. */}
            <FormControlLabel
              control={
                <GreenCheck
                  checked={sendCopyToSelf}
                  onChange={(event, checked) => setSendCopyToSelf(checked)}
                />
              }
              label={<Typography>Send a copy of this notification to me</Typography>}
            />
          </div>
        </div>

        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Add External Collaborators:</Typography>
          <div style={{ flexGrow: 1 }}>
            <div className={classes.emailEntry}>
              {/*
                multiline so a pasted list wraps and the field grows to fit it. A single-line field
                scrolls the start of the list out of view, which is where a mistyped address hides.
              */}
              <TextField
                multiline
                size="small"
                variant="outlined"
                placeholder="Email addresses, separated by , or ;"
                value={inputEmail}
                error={!!emailError}
                helperText={emailError}
                onChange={(e) => {
                  setEmailError('');
                  setInputEmail(e.target.value);
                }}
                onKeyDown={(e) => {
                  // Enter adds; Shift+Enter is left alone so a long list can still be broken up.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAddTypedEmails();
                  }
                }}
                className={classes.emailField}
              />
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<Add />}
                onClick={handleAddTypedEmails}
              >
                Add
              </Button>
            </div>
          </div>
        </div>

        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>To:</Typography>
          <div style={{ flexGrow: 1 }}>
            {recipients.length > 0 ? (
              <div className={classes.chips}>
                {recipients.map((recipient) => (
                  <RemovableChip
                    key={recipient.key}
                    typeIcon={recipient.icon}
                    label={recipient.label}
                    onRemove={recipient.remove}
                  />
                ))}
              </div>
            ) : (
              <Typography className={classes.emptyRecipients}>
                No recipients yet — add project team members or external collaborators above.
              </Typography>
            )}
          </div>
        </div>

        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Subject:</Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            inputProps={{ maxLength: 100 }}
          />
        </div>

        <div className={classes.fieldRow}>
          <Typography className={classes.fieldLabel}>Attached:</Typography>
          <div style={{ flexGrow: 1 }}>
            {attachments.length > 0 ? (
              <div className={classes.chips} style={{ marginBottom: 6 }}>
                {attachments.map((attachment) => (
                  <RemovableChip
                    key={attachment.key}
                    typeIcon={
                      attachment.documentId ? (
                        <Description fontSize="small" />
                      ) : (
                        <AttachFile fontSize="small" />
                      )
                    }
                    label={attachment.label}
                    onRemove={() =>
                      setAttachments((p) => p.filter((a) => a.key !== attachment.key))
                    }
                  />
                ))}
              </div>
            ) : null}
            <div className={classes.attachButtons}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disableElevation
                startIcon={<Description />}
                disabled={isLoadingTrees || documentTrees.length === 0}
                onClick={() => setDocumentDialogOpen(true)}
              >
                Attach Document
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disableElevation
                startIcon={<AttachFile />}
                onClick={() => setFileDialogOpen(true)}
              >
                Attach CENTERLINE Drive Files
              </Button>
            </div>
          </div>
        </div>

        <TextField
          fullWidth
          multiline
          rows={5}
          variant="outlined"
          placeholder="Write a message..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          style={{ marginTop: 12 }}
        />

        {/* Cancel pinned left, Send pinned right, so neither moves as the composer fills up. */}
        <div className={classes.sendRow}>
          <CancelButton onClick={clearComposer} disabled={isSending} />
          {isSending ? (
            <CircularLoader size={30} />
          ) : (
            <Button
              variant="contained"
              color="primary"
              disableElevation
              startIcon={<Send />}
              disabled={!canSend}
              onClick={handleSend}
            >
              Send
            </Button>
          )}
        </div>
      </CardContent>

      <SelectCompanyUsersDialog
        open={recipientDialogOpen}
        handleClose={() => setRecipientDialogOpen(false)}
        existingUserIds={[]}
        // No eligibleCompaniesOverride: that path takes the companies as given, and the project's
        // companies carry all of their users, not only the ones on this project. The default path
        // filters to project members, which is what makes a picked id safe to send as a team member.
        // Sharing is not tied to a document type, so the dialog's permission filter passes everyone.
        allowSelectingCurrentUser
        defaultSelectedUserIds={teamMemberIds}
        // The dialog's own manual-entry field is only rendered for PartiesPresent, so it never
        // returns typed addresses here. Outside addresses are added in External Collaborators.
        submit={(selectedUserIds) => {
          setTeamMemberIds(selectedUserIds);
          setRecipientDialogOpen(false);
        }}
      />

      {/* No documentId: with one set, Done writes the selection to that document's associations. */}
      <AssociateDocumentDialog
        open={documentDialogOpen}
        handleClose={() => setDocumentDialogOpen(false)}
        documents={documentTrees}
        setDocuments={setDocumentTrees}
        title="Attach Documents"
        subtitle="Pick the documents to attach. Recipients receive a link to each one."
        groupByPhase
        showStorytellingPhase={showStorytelling}
        onComplete={takeCheckedDocuments}
      />

      {/*
        The CENTERLINE Drive browser itself, rather than BrowseCenterlineDialog, whose only job
        is to ask which of the two sources you meant -- a question this card already answered with
        its own Attach Document and Attach File buttons. acceptAll without allowOnlyPdfs because
        anything in CENTERLINE Drive is worth sending, not just PDFs.
      */}
      <AddDesignFileDialog
        open={fileDialogOpen}
        handleClose={() => setFileDialogOpen(false)}
        title="CENTERLINE Drive"
        acceptAll
        handleDesignAttach={(fileNodes: FileNode[]) => {
          addAttachments(
            fileNodes.map((fileNode) => ({
              key: fileNode.fullKey,
              label: fileNode.fullKey.split('/').pop() || fileNode.fullKey,
              fileNode: { fullKey: fileNode.fullKey, ownerId: fileNode.ownerId },
            })),
          );
          setFileDialogOpen(false);
        }}
      />
    </Card>
  );
}
